/**
 * Utility functions for optimizing images from Supabase Storage
 * Uses Supabase's built-in image transformation API
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

interface ImageTransformOptions {
  width?: number;
  height?: number;
  quality?: number;
  format?: 'webp' | 'avif' | 'origin';
}

/**
 * Transform a Supabase storage URL to use image optimization
 * Only works for images stored in Supabase Storage
 */
export const getOptimizedImageUrl = (
  originalUrl: string | null,
  options: ImageTransformOptions = {}
): string | null => {
  if (!originalUrl) return null;
  
  // Only transform Supabase storage URLs
  if (!originalUrl.includes('supabase.co/storage/v1/object/public/')) {
    return originalUrl;
  }

  const { width, height, quality = 80, format = 'webp' } = options;

  // Convert public URL to render URL for transformations
  // From: .../storage/v1/object/public/bucket/path
  // To:   .../storage/v1/render/image/public/bucket/path?width=X&height=Y&quality=Q&format=F
  const transformedUrl = originalUrl.replace(
    '/storage/v1/object/public/',
    '/storage/v1/render/image/public/'
  );

  const params = new URLSearchParams();
  if (width) params.append('width', width.toString());
  if (height) params.append('height', height.toString());
  params.append('quality', quality.toString());
  params.append('format', format);

  return `${transformedUrl}?${params.toString()}`;
};

/**
 * Get optimized hero banner image URL based on device type
 * Uses aggressive compression for faster loading
 */
export const getOptimizedBannerUrl = (
  imageUrl: string | null,
  isMobile: boolean = false
): string | null => {
  if (!imageUrl) return null;

  // Mobile/portrait: optimize for smaller screens with aggressive compression
  if (isMobile) {
    return getOptimizedImageUrl(imageUrl, {
      width: 640,   // Reduced from 750 for faster loading
      quality: 70,  // Reduced from 75
      format: 'webp',
    });
  }

  // Desktop/landscape: balance quality vs speed
  return getOptimizedImageUrl(imageUrl, {
    width: 1280,  // Reduced from 1920 - still looks sharp, loads faster
    quality: 75,  // Reduced from 80
    format: 'webp',
  });
};

/**
 * Get srcset for responsive images
 */
export const getResponsiveSrcSet = (imageUrl: string | null): string => {
  if (!imageUrl || !imageUrl.includes('supabase.co/storage/v1/object/public/')) {
    return '';
  }

  const sizes = [640, 768, 1024, 1280, 1920];
  
  return sizes
    .map(width => {
      const optimizedUrl = getOptimizedImageUrl(imageUrl, {
        width,
        quality: 80,
        format: 'webp',
      });
      return `${optimizedUrl} ${width}w`;
    })
    .join(', ');
};
